import apiClient from './api';
import { ApiResponse } from '../types';

/**
 * The OAuth consent screen's API.
 *
 * These endpoints are not part of the OAuth protocol surface — no connector calls them. They
 * back the one page in this feature a customer ever sees, and they sit between the server's
 * /authorize handler and the client's redirect URI.
 */

export interface ConsentClient {
  name: string | null;
  uri: string | null;
  logoUri: string | null;
}

export interface ConsentRequestView {
  authenticated: boolean;
  client: ConsentClient;
  scopes: string[];
  user?: {
    email: string;
    name: string | null;
    organizationName: string | null;
  };
}

export const oauthService = {
  /**
   * Describe a pending authorization request.
   *
   * Returns 200 with `authenticated: false` for a signed-out visitor rather than 401 — the page
   * needs to render a sign-in form, not an error.
   */
  getConsentRequest: async (request: string): Promise<ConsentRequestView> => {
    const response = await apiClient.get<ApiResponse<ConsentRequestView>>('/auth/oauth/consent', {
      params: { request },
    });
    return response.data.data!;
  },

  /**
   * Submit the decision and get back where to send the browser.
   *
   * The response is a URL rather than a redirect: it must land in the address bar, so the page
   * assigns `window.location.href`. Letting axios follow a 302 would deliver the client's
   * callback inside an XHR, where the connector would never see it.
   */
  submitDecision: async (request: string, decision: 'allow' | 'deny'): Promise<string> => {
    const response = await apiClient.post<ApiResponse<{ redirectUrl: string }>>(
      '/auth/oauth/consent',
      { request, decision }
    );
    return response.data.data!.redirectUrl;
  },
};
